import { useState } from "react"

import useUser from "@hooks/useUser"

import { NO_REFERENCE_SENTINEL, findBrandSubscription, findCatalogSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"
import formatDate from "@utils/formatDate"

import { Skeleton } from "@particles/index"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { Prompt } from "@molecules/index"
import { PreviewModule } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useBrandCountQuery,
    useCatalogBrandsQuery,
    useCatalogResyncMutation,
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeFromCatalogMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"
import { CatalogBrandIdentity } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.types"
import CatalogBrandRow from "@pages/Settings/SettingsCatalogs/components/CatalogBrandRow/CatalogBrandRow"

import styles from "./CatalogCard.module.scss"
import { CatalogCardProps } from "./CatalogCard.types"

export default function CatalogCard(props: CatalogCardProps) {
    const { catalog } = props

    const { user } = useUser()

    const brandsQuery = useCatalogBrandsQuery(catalog.id)
    const brandCountQuery = useBrandCountQuery(catalog.id)
    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalog.id)

    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeFromCatalogMutation()
    const resyncMutation = useCatalogResyncMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isSyncing = catalog.last_sync_status === "in_progress"

    const lastSynced = catalog.last_sync_completed_at
        ? formatDate({
              datetime: catalog.last_sync_completed_at,
              preferredTimezone: user?.service_company?.preferred_timezone,
              addYear: true,
          }).localDateTimeString
        : null
    const syncCaption = isSyncing ? "Sync in progress…" : lastSynced ? `Last synced ${lastSynced}` : "Never synced"

    const subscriptions = subscriptionsQuery.data ?? []
    const isCatalogSubscribed = findCatalogSubscription(subscriptions) !== null

    const brandCounts = brandCountQuery.data ?? []
    const countByBrand = new Map(brandCounts.map((brandCount) => [brandCount.brand, brandCount]))

    // The brand listing carries only the real brands; the brandless "No Brand" bucket is a client-side
    // pseudo-row appended when the count payload reports active items with no brand.
    const brandIdentities: CatalogBrandIdentity[] = [...(brandsQuery.data ?? [])]
    if (countByBrand.has(NO_REFERENCE_SENTINEL)) {
        brandIdentities.push({ id: null, name: "No Brand", image_url: "" })
    }

    const isBrandRegionLoading = brandsQuery.isLoading || brandCountQuery.isLoading || subscriptionsQuery.isLoading

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        unsubscribeMutation.mutate(catalog)
    }

    const isActionPending = subscribeMutation.isPending || unsubscribeMutation.isPending || resyncMutation.isPending

    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                ...(isCatalogSubscribed
                    ? []
                    : [
                          {
                              label: "Subscribe to catalog",
                              icon: faIcon({ name: "circle-plus", style: "regular" as const }),
                              onClick: handleSubscribeClick,
                          },
                      ]),
                {
                    label: "Resync Catalog",
                    icon: faIcon({ name: "arrows-rotate", style: "regular" }),
                    onClick: () => resyncMutation.mutate(catalog),
                },
            ],
        },
        ...(isCatalogSubscribed
            ? [
                  { type: "divider" as const },
                  {
                      type: "group" as const,
                      items: [
                          {
                              variant: "danger" as const,
                              label: "Unsubscribe from catalog",
                              icon: faIcon({ name: "trash-can", style: "regular" as const }),
                              onClick: () => setIsUnsubscribePromptOpen(true),
                          },
                      ],
                  },
              ]
            : []),
    ]

    const previewCount = previewCountMutation.data?.count
    const ongoingImportClause = "new items added to this catalog later will be imported automatically too"
    const subscribeBody =
        previewCount === undefined
            ? `This will import this catalog's items into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`
            : `This will import ${previewCount} ${previewCount === 1 ? "item" : "items"} into your pricebook and keep them in sync — ${ongoingImportClause}. Are you sure you want to do this?`

    const renderBrandRegion = () => {
        if (isBrandRegionLoading) {
            return (
                <>
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                </>
            )
        }

        if (brandIdentities.length === 0) {
            return (
                <span className={styles.emptyMessage}>
                    No brands synced yet. Resync the catalog to pull in the store&apos;s brands and items.
                </span>
            )
        }

        return brandIdentities.map((brand) => {
            const count = countByBrand.get(brand.id ?? NO_REFERENCE_SENTINEL)
            const hasFullItemCoverage =
                count !== undefined && count.item_count > 0 && count.subscribed_item_count >= count.item_count
            const isFullySubscribed =
                isCatalogSubscribed || findBrandSubscription(subscriptions, brand.id) !== null || hasFullItemCoverage

            return (
                <CatalogBrandRow
                    key={brand.id ?? "no-brand"}
                    catalogId={catalog.id}
                    brand={brand}
                    count={count}
                    isFullySubscribed={isFullySubscribed}
                />
            )
        })
    }

    return (
        <>
            <PreviewModule
                className={styles.base}
                label={
                    <div className={styles.catalogIdentity}>
                        <Avatar
                            variant="company"
                            size="lg"
                            src={catalog.logo_url ?? undefined}
                            fallbackName={catalog.name}
                        />
                        <div className={styles.catalogIdentityText}>
                            <span className={styles.catalogName}>
                                {catalog.name}
                                {isCatalogSubscribed && (
                                    <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                        Subscribed
                                    </AlertBadge>
                                )}
                                {catalog.last_sync_status === "failed" && (
                                    <AlertBadge status="error" size="sm" showIcon={true} isLoading={false}>
                                        Sync failed
                                    </AlertBadge>
                                )}
                            </span>
                            <span className={styles.catalogCaption}>
                                {catalog.platform_display} • {syncCaption}
                            </span>
                        </div>
                    </div>
                }
                cta1={
                    <Menu
                        popoverSide="bottom"
                        popoverAlign="end"
                        title={catalog.name}
                        trigger={
                            <IconButton
                                variant="ghost"
                                size="md"
                                icon={faIcon({ name: "ellipsis", style: "regular" })}
                                aria-label="More options"
                                isProcessing={isActionPending}
                                isDisabled={isActionPending}
                            />
                        }
                        items={menuItems}
                    />
                }
                contentClassName={styles.brands}
            >
                {renderBrandRegion()}
            </PreviewModule>

            <Prompt
                title={`Subscribe to ${catalog.name}?`}
                body={subscribeBody}
                isOpen={isSubscribePromptOpen}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />

            <Prompt
                title={`Unsubscribe from ${catalog.name}?`}
                body="Items already in your pricebook will stay there, but they will no longer receive catalog updates, and new catalog items will no longer be imported automatically."
                isOpen={isUnsubscribePromptOpen}
                confirmText="Unsubscribe"
                onConfirm={handleUnsubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsUnsubscribePromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}
